import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import PackageFormModal from './PackageFormModal';
import AssignPackageModal from './AssignPackageModal';

const PackagesManagement = () => {
  const [packages, setPackages] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPackageFormModal, setShowPackageFormModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showAssignPackageModal, setShowAssignPackageModal] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const [packagesResponse, orgsResponse] = await Promise.all([
        axios.get(`${apiBase}/admin/packages`, { headers }),
        axios.get(`${apiBase}/admin/organizations`, { headers })
      ]);

      setPackages(packagesResponse.data);
      setOrganizations(orgsResponse.data);
    } catch (err) {
      console.error('Error loading packages data:', err);
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSavePackage = async (packageData) => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();

    if (selectedPackage) {
      await axios.put(`${apiBase}/admin/packages/${selectedPackage.id}`, packageData, { headers });
    } else {
      await axios.post(`${apiBase}/admin/packages`, packageData, { headers });
    }

    await loadData();
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to archive this package? It will no longer be available for new assignments.')) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/packages/${packageId}`, { headers });
      await loadData();
    } catch (err) {
      setError('Failed to archive package: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Access denied. Superadmin privileges required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Package Management</h1>
          <p className="text-gray-600">Create and manage subscription packages for organizations</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Packages</h2>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setShowAssignPackageModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium text-sm sm:text-base"
              >
                Assign Package
              </button>
              <button
                onClick={() => {
                  setSelectedPackage(null);
                  setShowPackageFormModal(true);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium text-sm sm:text-base"
              >
                + Create Package
              </button>
            </div>
          </div>
        </div>

        {packages.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No packages yet</h3>
            <p className="text-gray-500 mb-4">Create your first package to get started</p>
            <button
              onClick={() => {
                setSelectedPackage(null);
                setShowPackageFormModal(true);
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Create First Package
            </button>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block lg:hidden divide-y divide-gray-200">
              {packages.map((pkg) => (
                <div key={pkg.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-medium text-gray-900 truncate">{pkg.name}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          pkg.status === 'active' ? 'bg-green-100 text-green-800' :
                          pkg.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {pkg.status}
                        </span>
                        {pkg.is_trial && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            Trial
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{pkg.description || 'No description'}</p>
                      {pkg.internal_code && (
                        <p className="text-xs text-gray-400 mt-0.5">Code: {pkg.internal_code}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setShowPackageFormModal(true);
                        }}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                        title="Edit Package"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      {pkg.status !== 'archived' && (
                        <button
                          onClick={() => handleDeletePackage(pkg.id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                          title="Archive Package"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-3">
                    <span className="text-lg font-semibold text-gray-900">
                      {pkg.base_price > 0 ? `${pkg.base_price} ${pkg.currency}` : 'Free'}
                    </span>
                    {pkg.billing_period_days && pkg.billing_period_days !== 30 && (
                      <span className="text-xs text-gray-500 ml-1">/ {pkg.billing_period_days} days</span>
                    )}
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {pkg.teams_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">Teams</span>
                    )}
                    {pkg.flight_verification_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-cyan-100 text-cyan-800 rounded">Flight Verify</span>
                    )}
                    {pkg.flight_alerts_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">Alerts</span>
                    )}
                    {pkg.sso_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">SSO</span>
                    )}
                    {pkg.dedicated_emails_executives && (
                      <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded">Exec Email</span>
                    )}
                    {pkg.dedicated_emails_eas && (
                      <span className="px-2 py-0.5 text-xs bg-pink-100 text-pink-800 rounded">EA Email</span>
                    )}
                    {pkg.sticky_notes_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">Sticky Notes</span>
                    )}
                    {pkg.todos_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-teal-100 text-teal-800 rounded">Todos</span>
                    )}
                    {pkg.audit_logs_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-800 rounded">Audit Logs</span>
                    )}
                    {pkg.urbanride_quickbook_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">Urbanride</span>
                    )}
                  </div>

                  {/* Limits & Assignments */}
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                    <div>
                      {pkg.executive_config && (
                        <p>Execs: {pkg.executive_config.included_count} incl.</p>
                      )}
                      {pkg.ea_config && (
                        <p>EAs: {pkg.ea_config.limit_type === 'unlimited' ? '∞' :
                              pkg.ea_config.limit_type === 'fixed' ? `Max ${pkg.ea_config.max_count}` :
                              `${pkg.ea_config.included_count} incl.`}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p>Assignments: <span className="text-green-600 font-medium">{pkg.active_assignments || 0}</span> / {pkg.total_assignments || 0}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Features</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Limits</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignments</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {packages.map((pkg) => (
                    <tr key={pkg.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{pkg.name}</div>
                          <div className="text-sm text-gray-500">{pkg.description || 'No description'}</div>
                          {pkg.internal_code && (
                            <div className="text-xs text-gray-400">Code: {pkg.internal_code}</div>
                          )}
                          {pkg.is_trial && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 mt-1">
                              Trial {pkg.trial_days ? `(${pkg.trial_days} days)` : ''}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">
                            {pkg.base_price > 0 ? `${pkg.base_price} ${pkg.currency}` : 'Free'}
                          </div>
                          {pkg.billing_period_days && pkg.billing_period_days !== 30 && (
                            <div className="text-xs text-gray-500">per {pkg.billing_period_days} days</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {pkg.teams_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">Teams</span>
                          )}
                          {pkg.flight_verification_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-cyan-100 text-cyan-800 rounded">Flight Verify</span>
                          )}
                          {pkg.flight_alerts_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">Alerts</span>
                          )}
                          {pkg.sso_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">SSO</span>
                          )}
                          {pkg.dedicated_emails_executives && (
                            <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded">Exec Email</span>
                          )}
                          {pkg.dedicated_emails_eas && (
                            <span className="px-2 py-0.5 text-xs bg-pink-100 text-pink-800 rounded">EA Email</span>
                          )}
                          {pkg.sticky_notes_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">Sticky Notes</span>
                          )}
                          {pkg.todos_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-teal-100 text-teal-800 rounded">Todos</span>
                          )}
                          {pkg.audit_logs_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-800 rounded">Audit Logs</span>
                          )}
                          {pkg.urbanride_quickbook_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">Urbanride</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="space-y-1">
                          {pkg.executive_config && (
                            <div className="text-xs">
                              Execs: {pkg.executive_config.included_count} included
                              {pkg.executive_config.cost_per_additional > 0 && (
                                <span> (+${pkg.executive_config.cost_per_additional}/ea)</span>
                              )}
                            </div>
                          )}
                          {pkg.ea_config && (
                            <div className="text-xs">
                              EAs: {pkg.ea_config.limit_type === 'unlimited' ? 'Unlimited' :
                                    pkg.ea_config.limit_type === 'fixed' ? `Max ${pkg.ea_config.max_count}` :
                                    `${pkg.ea_config.included_count} included`}
                            </div>
                          )}
                          {pkg.document_parsing_config && (
                            <div className="text-xs">
                              Docs: {pkg.document_parsing_config.period === 'unlimited' ? 'Unlimited' :
                                     `${pkg.document_parsing_config.limit}/${pkg.document_parsing_config.period}`}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="text-green-600 font-medium">{pkg.active_assignments || 0}</span>
                        <span className="text-gray-400"> / {pkg.total_assignments || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          pkg.status === 'active' ? 'bg-green-100 text-green-800' :
                          pkg.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {pkg.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-row space-x-2 justify-center">
                          <button
                            onClick={() => {
                              setSelectedPackage(pkg);
                              setShowPackageFormModal(true);
                            }}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                            title="Edit Package"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {pkg.status !== 'archived' && (
                            <button
                              onClick={() => handleDeletePackage(pkg.id)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                              title="Archive Package"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Package Modals */}
      <PackageFormModal
        isOpen={showPackageFormModal}
        onClose={() => {
          setShowPackageFormModal(false);
          setSelectedPackage(null);
        }}
        onSave={handleSavePackage}
        existingPackage={selectedPackage}
      />

      <AssignPackageModal
        isOpen={showAssignPackageModal}
        onClose={() => setShowAssignPackageModal(false)}
        onSuccess={() => {
          loadData();
          setShowAssignPackageModal(false);
        }}
        organizations={organizations}
        packages={packages}
      />
    </div>
  );
};

export default PackagesManagement;
